import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@heroui/react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useInfiniteUserRecommendedCourses } from '@/hooks/queries/useCourses';
import CourseCard from '@/components/courses/CourseCard';
import CourseCardSkeleton from '@/components/courses/CourseCardSkeleton';

const RecommendedCoursesSection: React.FC = () => {
  const { t } = useTranslation('home');
  const { currentLanguage } = useLanguage();
  const language = currentLanguage || 'en';

  const {
    data: recommendedCoursesData,
    fetchNextPage: fetchNextRecommendedPage,
    hasNextPage: hasNextRecommendedPage,
    isFetchingNextPage: isFetchingNextRecommendedPage,
    isLoading: recommendedCoursesLoading,
  } = useInfiniteUserRecommendedCourses(language);

  // Flatten recommended courses
  const recommendedCourses = useMemo(() => {
    return (
      recommendedCoursesData?.pages.flatMap((page) => {
        const response = page.data;
        return response || [];
      }) || []
    );
  }, [recommendedCoursesData]);

  if (recommendedCourses.length === 0) {
    return null;
  }
  return (
    <motion.section
      className="mb-12"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.4 }}
    >
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {t('sections.recommendedCourses.title')}
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-400">{t('sections.recommendedCourses.subtitle')}</p>
      </div>

      {recommendedCoursesLoading && recommendedCourses.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <CourseCardSkeleton key={i} />
          ))}
        </div>
      ) : recommendedCourses.length === 0 ? (
        <div className="text-center py-12 bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl rounded-3xl border border-gray-200/50 dark:border-gray-800/50">
          <p className="text-gray-600 dark:text-gray-400">{t('noRecommendedCourses')}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {recommendedCourses.map((course) => (
              <CourseCard key={course.id} course={course} language={language} />
            ))}
          </div>

          {/* TODO: Add load more button */}
          {/* {hasNextRecommendedPage && (
            <div className="flex justify-center">
              <Button
                color="primary"
                variant="flat"
                onPress={() => fetchNextRecommendedPage()}
                isLoading={isFetchingNextRecommendedPage}
                className="min-w-32"
              >
                {t('loadMore')}
              </Button>
            </div>
          )} */}
        </>
      )}
    </motion.section>
  );
};

export default RecommendedCoursesSection;
